import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';
import { useAuth } from '../auth/AuthContext';
import { getPartnerEnquiryList } from '../services/subscriberWalletService';
import { formatDateTimeIST } from '../utils/dateFormatter';

const PAGE_SIZE = 25;

const PartnerEnquiryList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  const searchTimeoutRef = useRef(null);

  // Pagination
  const [page, setPage] = useState(() => {
    const p = searchParams.get('page');
    return p ? parseInt(p, 10) : 1;
  });
  const [pagination, setPagination] = useState({ page: 1, items: PAGE_SIZE, total: 0, total_pages: 0 });

  const lastUrlUpdateRef = useRef({ page: null, search: null });
  const lastApiCallRef = useRef({ page: null, search: null, role: null });
  const isLoadingRef = useRef(false);

  const currentRole = (() => {
    const authRole = localStorage.getItem('authRole');
    const roleMapping = {
      'wallet_superadmin': 'SuperAdmin',
      'wallet_finance': 'Finance',
      'wallet_ops': 'Ops',
      'wallet_support': 'Support',
    };
    return roleMapping[authRole] || null;
  })();

  const canAccess = currentRole === 'Ops' || currentRole === 'SuperAdmin';

  // Sync URL
  useEffect(() => {
    const newPage = page > 1 ? page.toString() : null;
    const newSearch = search.trim() || null;

    if (
      lastUrlUpdateRef.current.page !== newPage ||
      lastUrlUpdateRef.current.search !== newSearch
    ) {
      const params = new URLSearchParams();
      if (newPage) params.set('page', newPage);
      if (newSearch) params.set('search', newSearch);
      setSearchParams(params, { replace: true });
      lastUrlUpdateRef.current = { page: newPage, search: newSearch };
    }
  }, [page, search, setSearchParams]);

  // Debounce search
  const handleSearchSubmit = useCallback(() => {
    const trimmed = searchInput.trim();
    if (trimmed !== search) {
      setSearch(trimmed);
      setPage(1);
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
  }, [searchInput, search]);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(handleSearchSubmit, 2000);
    return () => clearTimeout(searchTimeoutRef.current);
  }, [searchInput, handleSearchSubmit]);

  // Load data
  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !currentRole) return;

    const currentPage = page || 1;
    const currentSearch = search.trim() || '';

    if (
      lastApiCallRef.current.page === currentPage &&
      lastApiCallRef.current.search === currentSearch &&
      lastApiCallRef.current.role === currentRole
    ) return;

    if (isLoadingRef.current) return;

    lastApiCallRef.current = { page: currentPage, search: currentSearch, role: currentRole };

    const loadData = async () => {
      isLoadingRef.current = true;
      try {
        setLoading(true);
        setError(null);

        const token = localStorage.getItem('authToken');
        if (!token) { navigate('/login'); return; }

        const params = { page: currentPage };
        if (currentSearch) {
          params.search = currentSearch;
          params.city = currentSearch;
          params.state = currentSearch;
        }

        const response = await getPartnerEnquiryList(currentRole, params);

        let items = null;
        let paginationData = null;

        if (response?.msg?.items) {
          items = response.msg.items;
          paginationData = response.msg.pagination;
        } else if (response?.items) {
          items = response.items;
          paginationData = response.pagination;
        }

        if (Array.isArray(items)) {
          setEnquiries(items);
          if (paginationData) {
            setPagination({
              ...paginationData,
              total_pages: Math.ceil(paginationData.total / paginationData.items),
            });
          }
        } else {
          setEnquiries([]);
        }
      } catch (err) {
        setError('Failed to load partner enquiries. Please try again.');
        setEnquiries([]);
      } finally {
        setLoading(false);
        isLoadingRef.current = false;
      }
    };

    loadData();
  }, [currentRole, isAuthenticated, authLoading, navigate, page, search]);

  if (!currentRole) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access the Subscriber Wallet.</p>
          <button onClick={() => navigate('/subscriber-wallet')} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Go to Home</button>
        </div>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">This feature is only available for Ops and SuperAdmin roles.</p>
          <button onClick={() => navigate('/subscriber-wallet')} className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Go to Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-7xl space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/subscriber-wallet')}
            className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600"
          >
            ← Back to Home
          </button>
        </div>

        <div className="bg-white rounded-md shadow-sm border border-gray-200">
          {/* Header + Search */}
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Ayushpay Leads</h2>
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by Name, Business, Mobile, Email, City, or State..."
                className="w-full pl-10 pr-4 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearchSubmit(); } }}
              />
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="px-6 py-4 text-center">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {/* Table */}
          {!loading && !error && (
            <>
              {enquiries.length === 0 ? (
                <div className="px-6 py-8 text-center text-gray-500">
                  <p className="mb-2">No partner enquiries found.</p>
                  <p className="text-sm text-gray-400">
                    {search.trim() ? 'Try adjusting your search.' : 'No enquiries submitted yet.'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full border border-gray-200 rounded-md min-w-max">
                      <thead className="bg-gray-50">
                        <tr className="border-b border-gray-200">
                          <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Date & Time</th>
                          <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Full Name</th>
                          <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Business Name</th>
                          <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Mobile</th>
                          <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Email</th>
                          <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">City</th>
                          <th className="px-3 sm:px-6 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">State</th>
                        </tr>
                      </thead>
                      <tbody>
                        {enquiries.map((item) => (
                          <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="px-3 sm:px-6 py-2 text-xs text-gray-500 whitespace-nowrap">
                              {item.created_at ? formatDateTimeIST(item.created_at) : 'N/A'}
                            </td>
                            <td className="px-3 sm:px-6 py-2 text-xs font-medium text-gray-900 whitespace-nowrap">{item.full_name.slice(0, 20) || 'N/A'}</td>
                            <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 whitespace-nowrap">{item.business_name || '—'}</td>
                            <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 whitespace-nowrap">{item.mobile || 'N/A'}</td>
                            <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 whitespace-nowrap">{item.email || '—'}</td>
                            <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 whitespace-nowrap">{item.city || '—'}</td>
                            <td className="px-3 sm:px-6 py-2 text-xs text-gray-700 whitespace-nowrap">{item.state || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {pagination.total > 0 && (
                    <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                      <div className="text-sm text-gray-500">
                        Showing {((pagination.page - 1) * pagination.items) + 1}–{Math.min(pagination.page * pagination.items, pagination.total)} of {pagination.total}
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          disabled={pagination.page === 1}
                          onClick={() => setPage(p => Math.max(1, p - 1))}
                          className={`px-3 py-1 border rounded-md text-sm ${pagination.page === 1 ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                        >
                          Prev
                        </button>
                        {Array.from({ length: pagination.total_pages || 1 }).map((_, i) => {
                          const pageNum = i + 1;
                          const totalPages = pagination.total_pages || 1;
                          const shouldShow = pageNum === 1 || pageNum === totalPages || (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2);
                          if (!shouldShow) {
                            if (pageNum === pagination.page - 3 || pageNum === pagination.page + 3) {
                              return <span key={`ellipsis-${i}`} className="px-2 text-gray-400">...</span>;
                            }
                            return null;
                          }
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setPage(pageNum)}
                              className={`px-3 py-1 border rounded-md text-sm ${pagination.page === pageNum ? 'bg-gray-900 text-white border-gray-900' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                        <button
                          disabled={pagination.page >= (pagination.total_pages || 1)}
                          onClick={() => setPage(p => Math.min(pagination.total_pages || 1, p + 1))}
                          className={`px-3 py-1 border rounded-md text-sm ${pagination.page >= (pagination.total_pages || 1) ? 'text-gray-400 border-gray-200 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-50 border-gray-300'}`}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PartnerEnquiryList;
